import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  FileText, Package, Calendar, User, MessageSquare, 
  Image as ImageIcon, Clock, ArrowLeft, Edit, Download,
  Mail, Bell, Users, Paperclip, File, Trash2, ExternalLink, MoreVertical, FlaskConical, Lock, LockOpen
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { toast } from 'sonner';
import { format, formatDistanceToNow } from 'date-fns';
import { cs } from 'date-fns/locale';
import { exportReportToPDF } from '../utils/exportUtils';
import Lightbox from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';

// Helper to format file size
const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

// Helper to get file icon based on type
const getFileIcon = (type) => {
  if (type?.startsWith('image/')) return '🖼️';
  if (type?.startsWith('video/')) return '🎬';
  if (type?.startsWith('audio/')) return '🎵';
  if (type?.includes('pdf')) return '📄';
  if (type?.includes('word') || type?.includes('document')) return '📝';
  if (type?.includes('excel') || type?.includes('spreadsheet')) return '📊';
  if (type?.includes('powerpoint') || type?.includes('presentation')) return '📽️';
  if (type?.includes('zip') || type?.includes('rar') || type?.includes('archive')) return '📦';
  return '📎';
};

export const ReportDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [report, setReport] = useState(null);
  const [comments, setComments] = useState([]);
  const [history, setHistory] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [uploadingFile, setUploadingFile] = useState(false);
  
  // Check if user is admin
  const isAdmin = user?.permissions?.includes('admin') || user?.role === 'admin';

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      const [reportRes, commentsRes, historyRes, statusesRes] = await Promise.all([
        api.get(`/reports/${id}`),
        api.get(`/reports/${id}/comments`),
        api.get(`/reports/${id}/history`),
        api.get('/statuses')
      ]);

      setReport(reportRes.data);
      setComments(commentsRes.data);
      setHistory(historyRes.data);
      setStatuses(statusesRes.data);
    } catch (error) {
      toast.error('Chyba při načítání hlášení');
      navigate('/reports');
    } finally {
      setLoading(false);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;

    try {
      const response = await api.post(`/reports/${id}/comments`, {
        content: newComment
      });
      setComments([...comments, response.data]);
      setNewComment('');
      toast.success('Komentář přidán');
    } catch (error) {
      toast.error('Chyba při přidávání komentáře');
    }
  };

  const handleStatusChange = async (newStatusId) => {
    try {
      const response = await api.put(`/reports/${id}`, {
        status_id: newStatusId
      });
      setReport(response.data);
      
      // Refresh history
      const historyRes = await api.get(`/reports/${id}/history`);
      setHistory(historyRes.data);
      
      toast.success('Stav aktualizován');
    } catch (error) {
      toast.error('Chyba při změně stavu');
    }
  };

  const handleToggleClosed = async () => {
    try {
      const response = await api.patch(`/reports/${id}/toggle-closed`);
      setReport(response.data);
      
      // Refresh history
      const historyRes = await api.get(`/reports/${id}/history`);
      setHistory(historyRes.data);
      
      toast.success(response.data.is_closed ? 'Hlášení uzavřeno' : 'Hlášení znovu otevřeno');
    } catch (error) {
      toast.error('Chyba při změně stavu');
    }
  };

  const handleDeleteReport = async () => {
    if (!window.confirm('Opravdu chcete smazat toto hlášení? Tato akce je nevratná a smaže i všechny komentáře a přílohy.')) return;
    
    try {
      await api.delete(`/reports/${id}`);
      toast.success('Hlášení smazáno');
      navigate('/reports');
    } catch (error) {
      toast.error('Chyba při mazání hlášení');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Soubor je příliš velký (max 10MB)', { duration: 3000 });
      return;
    }

    setUploadingFile(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      await api.post(`/reports/${id}/files`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      // Refresh report to get updated attachments
      const reportRes = await api.get(`/reports/${id}`);
      setReport(reportRes.data);
      
      // Refresh history
      const historyRes = await api.get(`/reports/${id}/history`);
      setHistory(historyRes.data);
      
      toast.success(`Soubor "${file.name}" nahrán`, { duration: 2000 });
    } catch (error) {
      toast.error('Chyba při nahrávání souboru', { duration: 3000 });
    } finally {
      setUploadingFile(false);
      // Reset input
      e.target.value = '';
    }
  };

  const handleDeleteFile = async (index, fileName) => {
    if (!window.confirm(`Opravdu chcete smazat soubor "${fileName}"?`)) return;
    
    try {
      await api.delete(`/reports/${id}/files/${index}`);
      
      // Refresh report
      const reportRes = await api.get(`/reports/${id}`);
      setReport(reportRes.data);
      
      // Refresh history
      const historyRes = await api.get(`/reports/${id}/history`);
      setHistory(historyRes.data);
      
      toast.success('Soubor smazán', { duration: 2000 });
    } catch (error) {
      toast.error('Chyba při mazání souboru', { duration: 3000 });
    }
  };

  const handleDownloadFile = (attachment) => {
    // Create download link
    const link = document.createElement('a');
    link.href = attachment.data;
    link.download = attachment.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExportPDF = () => {
    try {
      exportReportToPDF(report, comments, history);
      toast.success('PDF export dokončen', { duration: 2000 });
    } catch (error) {
      console.error('PDF export failed:', error);
      toast.error('Chyba při exportu PDF', { duration: 3000 });
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-full">
          <div className="text-slate-500">Načítání...</div>
        </div>
      </Layout>
    );
  }

  if (!report) return null;

  // Get only images from attachments for lightbox
  const imageAttachments = (report.attachments || []).filter(a => a.is_image);
  const lightboxSlides = imageAttachments.map(img => ({ src: img.data }));

  return (
    <Layout>
      <div className="p-8" data-testid="report-detail-page">
        {/* Header */}
        <div className="flex items-center justify-between gap-4 mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            data-testid="back-button"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Zpět
          </Button>
          <div className="flex gap-2">
            <Button
              onClick={handleToggleClosed}
              variant="outline"
              data-testid="toggle-closed-button"
              className={report.is_closed 
                ? "border-green-300 text-green-700 hover:bg-green-50" 
                : "border-orange-300 text-orange-700 hover:bg-orange-50"
              }
            >
              {report.is_closed ? (
                <>
                  <LockOpen className="h-4 w-4 mr-2" />
                  Znovu otevřít
                </>
              ) : (
                <>
                  <Lock className="h-4 w-4 mr-2" />
                  Uzavřít
                </>
              )}
            </Button>
            <Button
              onClick={() => navigate(`/reports/${id}/edit`)}
              variant="outline"
              data-testid="edit-report-button"
              className="border-cyan-300 text-cyan-700 hover:bg-cyan-50"
            >
              <Edit className="h-4 w-4 mr-2" />
              Upravit
            </Button>
            <Button
              onClick={handleExportPDF}
              variant="outline"
              data-testid="export-pdf-button"
              className="border-cyan-300 text-cyan-700 hover:bg-cyan-50"
            >
              <Download className="h-4 w-4 mr-2" />
              Exportovat PDF
            </Button>
            {/* Admin delete action */}
            {isAdmin && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="icon">
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={handleDeleteReport} className="text-red-600">
                    <Trash2 className="h-4 w-4 mr-2" />
                    Smazat hlášení
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>

        {/* Closed banner */}
        {report.is_closed && (
          <div className="bg-slate-100 border border-slate-300 rounded-xl p-4 mb-6 flex items-center gap-3">
            <Lock className="h-5 w-5 text-slate-500" />
            <div>
              <span className="font-semibold text-slate-700">Toto hlášení je uzavřeno</span>
              {report.closed_by_name && report.closed_at && (
                <p className="text-sm text-slate-500">
                  Uzavřel: {report.closed_by_name} • {format(new Date(report.closed_at), 'dd.MM.yyyy HH:mm', { locale: cs })}
                </p>
              )}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Details & Gallery */}
          <div className="lg:col-span-2 space-y-6">
            {/* Report Details */}
            <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm" data-testid="report-details">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h1 className="text-2xl font-bold text-slate-900 mb-2">{report.title}</h1>
                  <div className="flex items-center gap-4 text-sm text-slate-600">
                    <div className="flex items-center gap-1">
                      <User className="h-4 w-4" />
                      {report.created_by_name}
                    </div>
                    <div className="flex items-center gap-1">
                      <Calendar className="h-4 w-4" />
                      {format(new Date(report.created_at), 'dd.MM.yyyy', { locale: cs })}
                    </div>
                  </div>
                </div>
                <Badge
                  style={{ backgroundColor: report.status_color + '20', color: report.status_color }}
                  className="font-semibold text-sm px-3 py-1"
                  data-testid="report-status-badge"
                >
                  {report.status_name}
                </Badge>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-6">
                <div>
                  <Label className="text-slate-500 text-xs uppercase mb-1">Šarže</Label>
                  <div className="flex items-center gap-2 text-slate-900 font-medium">
                    <Package className="h-4 w-4 text-slate-400" />
                    {report.batch}
                  </div>
                </div>
                <div>
                  <Label className="text-slate-500 text-xs uppercase mb-1">Počet kusů</Label>
                  <div className="text-slate-900 font-medium">{report.quantity}</div>
                </div>
                <div>
                  <Label className="text-slate-500 text-xs uppercase mb-1">Kategorie</Label>
                  <div className="text-slate-900 font-medium">
                    {report.category_name || 'Bez kategorie'}
                  </div>
                </div>
                {report.source_sensory_test_id && (
                  <div className="col-span-2">
                    <Label className="text-slate-500 text-xs uppercase mb-1">Vytvořeno ze senzorické zkoušky</Label>
                    <Link 
                      to={`/senzorika/${report.source_sensory_test_id}`}
                      className="flex items-center gap-2 text-purple-600 hover:text-purple-800 font-medium"
                    >
                      <FlaskConical className="h-4 w-4" />
                      <span className="hover:underline">Zobrazit senzorickou zkoušku</span>
                      <ExternalLink className="h-3 w-3" />
                    </Link>
                  </div>
                )}
              </div>

              {/* Custom Fields */}
              {report.custom_fields && Object.keys(report.custom_fields).length > 0 && (
                <div className="border-t border-slate-200 pt-4">
                  <h3 className="text-sm font-semibold text-slate-900 mb-3">Další informace</h3>
                  <div className="grid grid-cols-2 gap-4">
                    {Object.entries(report.custom_fields).map(([key, value]) => (
                      <div key={key}>
                        <Label className="text-slate-500 text-xs mb-1">{key}</Label>
                        <div className="text-slate-900">{value}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Status Change */}
              <div className="border-t border-slate-200 pt-4 mt-4">
                <Label htmlFor="status-select" className="text-sm font-medium mb-2">Změnit stav</Label>
                <Select value={report.status_id} onValueChange={handleStatusChange}>
                  <SelectTrigger id="status-select" data-testid="status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {statuses.map(status => (
                      <SelectItem key={status.id} value={status.id}>
                        <div className="flex items-center gap-2">
                          <div
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: status.color }}
                          />
                          {status.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Attachments Section */}
            <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm" data-testid="report-attachments">
              <h3 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Paperclip className="h-5 w-5" />
                Přílohy
              </h3>
              
              {/* Upload */}
              <div className="mb-4">
                <label className="flex items-center justify-center w-full h-24 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-cyan-400 hover:bg-cyan-50/50 transition-colors">
                  <div className="text-center">
                    <Paperclip className="h-6 w-6 mx-auto text-slate-400 mb-1" />
                    <span className="text-sm text-slate-500">
                      {uploadingFile ? 'Nahrávání...' : 'Klikněte pro nahrání souboru'}
                    </span>
                    <p className="text-xs text-slate-400 mt-1">Max. 10 MB</p>
                  </div>
                  <Input
                    type="file"
                    onChange={handleFileUpload}
                    disabled={uploadingFile}
                    data-testid="file-upload-input"
                    className="hidden"
                  />
                </label>
              </div>

              {/* Attachments List */}
              {report.attachments && report.attachments.length > 0 ? (
                <div className="space-y-2">
                  {/* Images Grid */}
                  {report.attachments.filter(a => a.is_image).length > 0 && (
                    <div className="mb-4">
                      <p className="text-sm font-medium text-slate-600 mb-2">Obrázky</p>
                      <div className="grid grid-cols-3 gap-3">
                        {report.attachments.filter(a => a.is_image).map((attachment, idx) => {
                          const imageIndex = report.attachments.filter(a => a.is_image).findIndex((a, i) => i === idx);
                          const globalIndex = report.attachments.indexOf(attachment);
                          return (
                            <div
                              key={globalIndex}
                              className="aspect-square rounded-lg overflow-hidden border border-slate-200 cursor-pointer hover:opacity-80 transition-opacity group relative"
                              data-testid={`gallery-image-${globalIndex}`}
                            >
                              <img
                                src={attachment.data}
                                alt={attachment.name}
                                className="w-full h-full object-cover"
                                onClick={() => {
                                  setLightboxIndex(imageIndex);
                                  setLightboxOpen(true);
                                }}
                              />
                              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center gap-2">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setLightboxIndex(imageIndex);
                                    setLightboxOpen(true);
                                  }}
                                  className="p-1.5 bg-white/90 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                  <ExternalLink className="h-4 w-4 text-slate-700" />
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteFile(globalIndex, attachment.name);
                                  }}
                                  className="p-1.5 bg-white/90 rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-100"
                                >
                                  <Trash2 className="h-4 w-4 text-red-600" />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Other Files List */}
                  {report.attachments.filter(a => !a.is_image).length > 0 && (
                    <div>
                      <p className="text-sm font-medium text-slate-600 mb-2">Dokumenty a soubory</p>
                      <div className="space-y-2">
                        {report.attachments.filter(a => !a.is_image).map((attachment) => {
                          const globalIndex = report.attachments.indexOf(attachment);
                          return (
                            <div
                              key={globalIndex}
                              className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200 group hover:border-cyan-300 transition-colors"
                              data-testid={`file-attachment-${globalIndex}`}
                            >
                              <span className="text-2xl">{getFileIcon(attachment.type)}</span>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-slate-800 truncate">{attachment.name}</p>
                                <p className="text-xs text-slate-500">
                                  {formatFileSize(attachment.size)} • {attachment.type?.split('/')[1]?.toUpperCase() || 'Soubor'}
                                </p>
                              </div>
                              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDownloadFile(attachment)}
                                  className="h-8 w-8 p-0"
                                  title="Stáhnout"
                                >
                                  <Download className="h-4 w-4 text-slate-600" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteFile(globalIndex, attachment.name)}
                                  className="h-8 w-8 p-0 hover:bg-red-100"
                                  title="Smazat"
                                >
                                  <Trash2 className="h-4 w-4 text-red-600" />
                                </Button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-400">
                  <Paperclip className="h-12 w-12 mx-auto mb-2" />
                  <p>Zatím žádné přílohy</p>
                </div>
              )}
            </div>
          </div>

          {/* Right Column - Comments & History */}
          <div className="space-y-6">
            {/* Comments */}
            <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm" data-testid="comments-section">
              <h3 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <MessageSquare className="h-5 w-5" />
                Komentáře
              </h3>

              {/* Add Comment */}
              <div className="mb-4">
                <Textarea
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Napište komentář..."
                  className="mb-2"
                  data-testid="comment-textarea"
                />
                <Button
                  onClick={handleAddComment}
                  disabled={!newComment.trim()}
                  className="w-full bg-slate-900 hover:bg-slate-800"
                  data-testid="add-comment-button"
                >
                  Přidat komentář
                </Button>
              </div>

              {/* Comments List */}
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {comments.length === 0 ? (
                  <p className="text-slate-400 text-sm text-center py-4">Zatím žádné komentáře</p>
                ) : (
                  comments.map((comment) => (
                    <div
                      key={comment.id}
                      className="bg-slate-50 rounded-lg p-3"
                      data-testid={`comment-${comment.id}`}
                    >
                      <div className="flex items-start gap-2">
                        <div className="w-8 h-8 rounded-full bg-slate-900 text-white flex items-center justify-center text-xs font-semibold flex-shrink-0">
                          {comment.user_name.charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-baseline gap-2 mb-1">
                            <span className="font-medium text-slate-900 text-sm">
                              {comment.user_name}
                            </span>
                            <span className="text-xs text-slate-500">
                              {format(new Date(comment.created_at), 'dd.MM.yyyy HH:mm', { locale: cs })}
                            </span>
                          </div>
                          <p className="text-sm text-slate-700 whitespace-pre-wrap">
                            {comment.content}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Notifications Info */}
            <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm" data-testid="notifications-section">
              <h3 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Bell className="h-5 w-5 text-amber-500" />
                Nastavení notifikací
              </h3>

              <div className="space-y-4">
                {/* Report change notifications */}
                <div className="border-b border-slate-100 pb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Mail className="h-4 w-4 text-cyan-500" />
                    <span className="font-medium text-slate-700">Notifikace při změnách záznamu</span>
                  </div>
                  {report.notification_emails && report.notification_emails.length > 0 ? (
                    <div className="ml-6">
                      <p className="text-sm text-slate-500 mb-2">Příjemci notifikací:</p>
                      <div className="flex flex-wrap gap-1">
                        {report.notification_emails.map((email) => (
                          <Badge key={email} variant="secondary" className="bg-cyan-50 text-cyan-700 text-xs">
                            {email}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-slate-400 ml-6">Žádné notifikační emaily nenastaveny</p>
                  )}
                  <p className="text-xs text-slate-400 ml-6 mt-1">
                    + autor záznamu ({report.created_by_name}) je vždy notifikován
                  </p>
                </div>

                {/* DMT notifications */}
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Clock className="h-4 w-4 text-amber-500" />
                    <span className="font-medium text-slate-700">Notifikace končícího DMT</span>
                  </div>
                  {report.dmt_notification && report.dmt_notification.enabled ? (
                    <div className="ml-6 bg-amber-50 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge className="bg-amber-100 text-amber-700">
                          {report.dmt_notification.days_before} dní před koncem DMT
                        </Badge>
                        {report.dmt_notification.last_notified && (
                          <span className="text-xs text-slate-500">
                            (naposledy odesláno: {report.dmt_notification.last_notified})
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-slate-500 mb-1">Příjemci:</p>
                      <div className="flex flex-wrap gap-1">
                        {report.dmt_notification.emails?.map((email) => (
                          <Badge key={email} variant="secondary" className="bg-amber-100 text-amber-700 text-xs">
                            {email}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-slate-400 ml-6">DMT notifikace není nastavena</p>
                  )}
                </div>
              </div>
            </div>

            {/* History */}
            <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm" data-testid="history-section">
              <h3 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Historie změn
              </h3>

              <div className="space-y-3">
                {history.length === 0 ? (
                  <p className="text-slate-400 text-sm text-center py-4">Žádná historie</p>
                ) : (
                  history.map((item, index) => (
                    <div
                      key={item.id}
                      className="relative pl-6 pb-3"
                      data-testid={`history-item-${item.id}`}
                    >
                      {index !== history.length - 1 && (
                        <div className="absolute left-2 top-6 bottom-0 w-0.5 bg-slate-200" />
                      )}
                      <div 
                        className={`absolute left-0 top-1 w-4 h-4 rounded-full ${
                          item.change_type === 'create' ? 'bg-emerald-500' :
                          item.change_type === 'status' ? 'bg-cyan-500' :
                          item.change_type === 'image' ? 'bg-violet-500' :
                          item.change_type === 'file' ? 'bg-blue-500' :
                          item.change_type === 'comment' ? 'bg-purple-500' :
                          'bg-amber-500'
                        }`} 
                      />
                      <div>
                        <div className="text-sm text-slate-900 font-medium">
                          {item.change_type === 'create' && (
                            <>Hlášení vytvořeno se stavem <span className="font-semibold text-emerald-600">{item.new_status}</span></>
                          )}
                          {item.change_type === 'status' && (
                            <>
                              Změna stavu: <span className="font-semibold text-slate-500">{item.old_status}</span>
                              {' → '}
                              <span className="font-semibold text-cyan-600">{item.new_status}</span>
                            </>
                          )}
                          {item.change_type === 'edit' && (
                            <span className="text-amber-700">{item.description || 'Úprava hlášení'}</span>
                          )}
                          {item.change_type === 'image' && (
                            <span className="text-violet-600">{item.description || 'Nahrán obrázek'}</span>
                          )}
                          {item.change_type === 'file' && (
                            <span className="text-blue-600">{item.description || 'Nahrán soubor'}</span>
                          )}
                          {item.change_type === 'comment' && (
                            <span className="text-purple-600">{item.description || 'Přidán komentář'}</span>
                          )}
                          {/* Fallback for old history entries */}
                          {!item.change_type && (
                            item.old_status ? (
                              <>
                                Změna stavu z <span className="font-semibold">{item.old_status}</span> na{' '}
                                <span className="font-semibold">{item.new_status}</span>
                              </>
                            ) : (
                              <>Hlášení vytvořeno se stavem <span className="font-semibold">{item.new_status}</span></>
                            )
                          )}
                        </div>
                        <div className="text-xs text-slate-500 mt-1">
                          {item.changed_by_name} •{' '}
                          {format(new Date(item.changed_at), 'dd.MM.yyyy HH:mm', { locale: cs })}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Lightbox */}
        {lightboxOpen && (
          <Lightbox
            open={lightboxOpen}
            close={() => setLightboxOpen(false)}
            index={lightboxIndex}
            slides={lightboxSlides}
          />
        )}
      </div>
    </Layout>
  );
};
